import HeroCarousel from "@/components/home/HeroCarousel";
import ServicesGrid from "@/components/home/ServicesGrid";
import WhyChooseUs from "@/components/home/WhyChooseUs";
import AboutOverview from "@/components/home/AboutOverview";
import BankPartners from "@/components/home/BankPartners";
import Gallery from "@/components/home/Gallery";
import ReviewsSection from "@/components/home/ReviewsSection";
import FaqSection from "@/components/home/FaqSection";
import ContactMap from "@/components/home/ContactMap";
import OrderForm from "@/components/OrderForm";
import { useReveal } from "@/hooks/use-reveal";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, Zap, ShieldCheck, Timer, Sparkles } from "lucide-react";
import { BUSINESS } from "@/lib/constants";
import { HOME_IDS } from "@/constants/testIds/site";

function HeroCTAStrip() {
  return (
    <section className="relative -mt-4 sm:-mt-6 z-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-xl px-5 sm:px-8 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="hidden sm:grid w-12 h-12 rounded-xl bg-[#F59E0B]/15 text-[#B45309] place-items-center shrink-0">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-[#0E7490]">Instant 15-Minute Activation</div>
              <div className="font-display text-lg sm:text-xl font-bold text-[#111827] leading-tight">
                Buy, Recharge or Unblock your FASTag today
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button asChild className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full btn-brand h-11 px-6 shadow active:scale-95">
              <Link to="/order" data-testid={HOME_IDS.heroOrder}>Order FASTag</Link>
            </Button>
            <Button asChild className="bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full btn-brand h-11 px-6 shadow active:scale-95">
              <a href={`tel:${BUSINESS.phone}`} data-testid={HOME_IDS.heroCta}>
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </Button>
            <Button asChild variant="outline" className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366] hover:text-white rounded-full btn-brand h-11 px-6">
              <a href={`https://wa.me/${BUSINESS.whatsapp}`} target="_blank" rel="noreferrer" data-testid={HOME_IDS.heroWhatsapp}>
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </a>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {[
            { icon: Zap, label: "Instant Activation", sub: "In 15 minutes" },
            { icon: ShieldCheck, label: "100% Secure", sub: "Safe & reliable" },
            { icon: Timer, label: "Same-day Service", sub: "At our counter" },
            { icon: Sparkles, label: "All Major Banks", sub: "IDFC · ICICI · Bajaj · Airtel · +" },
          ].map((it, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex items-center gap-3 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#0057B8]/10 text-[#0057B8] grid place-items-center shrink-0"><it.icon className="w-5 h-5" /></div>
              <div>
                <div className="font-semibold text-[#111827] text-sm">{it.label}</div>
                <div className="text-xs text-[#6B7280]">{it.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OrderStrip() {
  const r = useReveal();
  return (
    <section id="order" className="py-20 sm:py-24 bg-[#0057B8] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #F59E0B 0, transparent 45%), radial-gradient(circle at 85% 70%, #0E7490 0, transparent 45%)" }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid lg:grid-cols-12 gap-10 items-start">
        <div ref={r.ref} className={`${r.className} lg:col-span-5`}>
          <span className="text-sm font-semibold uppercase tracking-widest text-[#F59E0B]">Order FASTag</span>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
            Delivered across <span className="text-[#F59E0B]">all 23 WB districts</span>
          </h2>
          <p className="mt-4 text-white/85 text-base sm:text-lg leading-relaxed">
            Share your vehicle and address details — our team will verify KYC and dispatch your FASTag to any district in West Bengal.
          </p>
          <ul className="mt-6 space-y-2 text-white/85">
            <li>• Statewide delivery — Kolkata, Howrah, Darjeeling and beyond</li>
            <li>• Assistance for cars, LCVs, trucks, buses and taxis</li>
            <li>• Multi-bank FASTag issuance & activation</li>
          </ul>
        </div>
        <div className="lg:col-span-7 text-[#111827]">
          <OrderForm />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <HeroCarousel />
      <HeroCTAStrip />
      <ServicesGrid />
      <BankPartners />
      <WhyChooseUs />
      <AboutOverview />
      <OrderStrip />
      <Gallery />
      <ReviewsSection />
      <FaqSection />
      <ContactMap />
    </>
  );
}
